import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Database, Trash2, Target, ChevronDown, ChevronRight, CalendarDays } from "lucide-react";
import { TIER_BY_KEY } from "@/lib/game";
import PairingSequence from "@/components/PairingSequence";
import { api } from "@/lib/api";
import { useTeam } from "@/lib/team";

const RESULTS = [
  { key: "win", label: "W", color: "text-emerald-400 border-emerald-600" },
  { key: "loss", label: "L", color: "text-red-400 border-red-600" },
];

function accuracy(pairings) {
  let graded = 0, correct = 0;
  pairings.forEach((p) => {
    if (p.actual === "win" || p.actual === "loss") {
      graded++;
      const predWin = (p.predicted_value || 0) >= 0;
      if ((predWin && p.actual === "win") || (!predWin && p.actual === "loss")) correct++;
    }
  });
  return { graded, correct };
}

export default function HistoryTab() {
  const { activeId } = useTeam();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState({});

  const load = () => api.teamHistory(activeId).then(setItems);
  useEffect(() => { load(); }, [activeId]); // eslint-disable-line

  const setActual = async (entry, idx, val) => {
    const pairings = entry.pairings.map((p, i) => (i === idx ? { ...p, actual: p.actual === val ? "" : val } : p));
    setItems((its) => its.map((e) => (e.id === entry.id ? { ...e, pairings } : e)));
    await api.teamUpdateHistory(activeId, entry.id, { pairings });
  };
  const remove = async (id) => {
    await api.teamDelHistory(activeId, id);
    setItems((its) => its.filter((e) => e.id !== id));
    toast.success("Round deleted");
  };

  if (!items) return <p className="text-slate-500 font-mono text-sm">Loading…</p>;
  if (items.length === 0) {
    return (
      <Card className="bg-[#121824]/70 border-slate-800 p-10 text-center">
        <Database className="h-8 w-8 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-300 font-semibold">No saved rounds yet.</p>
        <p className="text-slate-500 text-sm mt-1">Complete a pairing in the Live Resolver and save it — rounds are grouped by event here.</p>
      </Card>
    );
  }

  // group by event name
  const groups = {};
  items.forEach((e) => {
    const key = e.event_name || "Untitled event";
    (groups[key] = groups[key] || []).push(e);
  });
  const eventNames = Object.keys(groups);

  return (
    <div className="space-y-6">
      {eventNames.map((ev) => {
        const rounds = [...groups[ev]].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        let gWins = 0, gLoss = 0, gGraded = 0, gCorrect = 0;
        rounds.forEach((r) => {
          gWins += r.pairings.filter((p) => p.actual === "win").length;
          gLoss += r.pairings.filter((p) => p.actual === "loss").length;
          const a = accuracy(r.pairings); gGraded += a.graded; gCorrect += a.correct;
        });
        return (
          <div key={ev} className="space-y-3" data-testid="history-event-group">
            <div className="flex items-center justify-between gap-3 flex-wrap border-b border-slate-800 pb-2">
              <h2 className="font-display font-black uppercase tracking-tight text-lg flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-amber-400" /> {ev}
                <span className="text-xs font-mono text-slate-500 font-normal">{rounds.length} round{rounds.length > 1 ? "s" : ""}</span>
              </h2>
              <div className="flex items-center gap-5">
                <div className="text-right">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Event record</p>
                  <p className="font-mono text-lg font-bold"><span className="text-emerald-400">{gWins}</span><span className="text-slate-600">–</span><span className="text-red-400">{gLoss}</span></p>
                </div>
                {gGraded > 0 && (
                  <div className="text-right">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-1"><Target className="h-3 w-3" /> Prediction accuracy</p>
                    <p className="font-mono text-lg font-bold text-cyan-400">{gCorrect}/{gGraded} <span className="text-xs text-slate-500">({Math.round((gCorrect / gGraded) * 100)}%)</span></p>
                  </div>
                )}
              </div>
            </div>

            {rounds.map((entry) => {
              const isOpen = open[entry.id] !== false;
              const myWins = entry.pairings.filter((p) => p.actual === "win").length;
              const myLosses = entry.pairings.filter((p) => p.actual === "loss").length;
              return (
                <Card key={entry.id} className="bg-[#121824]/70 border-slate-800 p-4 rise" data-testid="history-entry">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button onClick={() => setOpen((o) => ({ ...o, [entry.id]: !isOpen }))} className="flex items-center gap-2 text-left">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                      <div>
                        <p className="font-semibold text-slate-100">{entry.round_name || "Round"} · {entry.team_a_name} <span className="text-slate-600">vs</span> {entry.team_b_name}</p>
                        <p className="text-[11px] text-slate-500 font-mono">you were Team {entry.you_are} · {new Date(entry.created_at).toLocaleString()}</p>
                      </div>
                    </button>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Actual</p>
                        <p className="font-mono text-base font-bold"><span className="text-emerald-400">{myWins}</span><span className="text-slate-600">–</span><span className="text-red-400">{myLosses}</span></p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Predicted</p>
                        <p className="font-mono text-base font-bold text-amber-400">{entry.predicted_score > 0 ? `+${entry.predicted_score}` : entry.predicted_score}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => remove(entry.id)} className="text-slate-500 hover:text-red-400" data-testid="delete-history-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="space-y-2 mt-3">
                      {entry.pairings.map((p, i) => {
                        const tier = p.predicted_tier ? TIER_BY_KEY[p.predicted_tier] : null;
                        return (
                          <div key={i} className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2">
                            <span className="text-xs font-mono text-slate-600 w-5">{i + 1}</span>
                            <div className="flex-1 min-w-0 flex items-center gap-2 text-sm">
                              <span className="text-amber-300 font-semibold truncate">{p.a_name}</span>
                              <span className="text-slate-600 text-xs">vs</span>
                              <span className="text-cyan-300 font-semibold truncate">{p.b_name}</span>
                            </div>
                            {p.table && <span className="text-[11px] font-mono text-slate-500">T{p.table}</span>}
                            {tier && <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded" style={{ background: tier.bg, color: tier.text }}>{tier.code}</span>}
                            <div className="flex gap-1">
                              {RESULTS.map((r) => (
                                <button key={r.key} onClick={() => setActual(entry, i, r.key)} data-testid={`result-${r.key}-btn`}
                                  className={`h-7 w-7 rounded border text-xs font-bold transition-colors ${p.actual === r.key ? `${r.color} bg-slate-800` : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>
                                  {r.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {entry.sequence?.length > 1 && (
                        <div className="mt-3 pt-3 border-t border-slate-800">
                          <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Final pairing sequence</p>
                          <PairingSequence sequence={entry.sequence} youAre={entry.you_are}
                            teamAName={entry.team_a_name} teamBName={entry.team_b_name} routeMode scale={0.82} />
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
